import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import ResizableDrawer from 'components/common/ResizableDrawer';
import {
  AppBar,
  Toolbar,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@material-ui/core';
import { always } from 'ramda';
import { makeStyles } from '@material-ui/core/styles';
import { selector } from 'selectors/payerNotes';
import { useSelector, useDispatch } from 'react-redux';
import TablePagination from '@material-ui/core/TablePagination';
import Button from '@material-ui/core/Button';
import { RegularNoteType } from '../../../../../common/Note/consts';
import NoteWriter from './NoteWriter';

const useStyles = makeStyles((theme) => {
  const toolbarMinHeight =
    theme.mixins.toolbar[theme.breakpoints.up('sm')]?.minHeight ??
    theme.mixins.toolbar.minHeight ??
    0;

  return {
    appBarPrimaryOverrideColor: {
      backgroundColor: theme.palette.error.light,
    },
    drawer: {
      top: toolbarMinHeight,
      height: `calc(100% - ${toolbarMinHeight}px)`,
      borderLeft: `1px solid ${theme.palette.divider}`,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    content: {
      display: 'flex',
      flex: '1 1 auto',
      flexDirection: 'column',
      overflow: 'auto',
      position: 'relative',
    },
    flex: {
      flex: '1 1 auto',
    },
    noNotesContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
      padding: theme.spacing(2),
    },
    buttonContainer: {
      display: 'flex',
      flex: '1 1 auto',
      justifyContent: 'flex-end',
      marginTop: theme.spacing(1),
    },
    table: {
      margin: theme.spacing(2),
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: '4px',
      overflow: 'hidden',
    },
    tableHead: {
      backgroundColor: 'white',
    },
    tableRow: {
      '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
      },
      cursor: 'default',
      '&:hover': {
        cursor: 'default',
        backgroundColor: theme.palette.action.hover,
      },
    },
    tableCell: {
      padding: theme.spacing(1.5),
      fontSize: '0.9rem',
    },
    addButton: {
      backgroundColor: theme.palette.primary.light, // Purple background matching the theme
      color: theme.palette.common.white,
      '&:hover': {
        backgroundColor: theme.palette.primary.main, // Darker purple on hover
      },
    },
    cancelButton: {
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.text.primary,
      '&:hover': {
        backgroundColor: theme.palette.grey[400],
      },
    },
  };
});

const NoteDrawer = ({ fspId, payerId, onClose, open }) => {
  const dispatch = useDispatch();
  const items = useSelector((state) => selector(state, { payerId }));
  const [noteType, setNoteType] = useState(items?.noteType);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortColumn, setSortColumn] = useState('id');
  const [sortOrder, setSortOrder] = useState('asc');
  const [openNoteWriter, setOpenNoteWriter] = useState(false);
  const classes = useStyles();

  const isRegularNote = noteType === RegularNoteType;

  const handleClose = (event) => {
    event.stopPropagation();
    onClose(event, true);
  };

  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortOrder === 'asc';
    setSortOrder(isAsc ? 'desc' : 'asc');
    setSortColumn(column);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAddNote = (event) => {
    event.stopPropagation();
    setOpenNoteWriter(true);
  };

  const handleCloseNoteWriter = () => {
    setOpenNoteWriter(false);
  };

  const handleDrawerClose = (event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
      setOpenNoteWriter(false);
      onClose(event, true);
    }
  };

  useEffect(() => {
    if (!open) {
      setOpenNoteWriter(false);
    }
  }, [open]);

  const sortedItems = [...items].sort((a, b) => {
    if (a[sortColumn] < b[sortColumn]) return sortOrder === 'asc' ? -1 : 1;
    if (a[sortColumn] > b[sortColumn]) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  const paginatedItems = sortedItems.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <ResizableDrawer
      anchor="right"
      classes={{ paper: classes.drawer }}
      maxWidth="720px"
      minWidth={400}
      open={open}
      variant="temporary"
      width="50%"
      onClose={handleDrawerClose}
      onClick={(event) => event.stopPropagation()}
    >
      {!openNoteWriter && (
        <AppBar color="inherit" position="static">
          <Toolbar>
            <div className={classes.buttonContainer}>
              <Button
                aria-label="Add Note"
                className={classes.addButton}
                onClick={handleAddNote}
              >
                Add Note
              </Button>
              <Button className={classes.cancelButton} onClick={handleClose}>
                Close
              </Button>
            </div>
          </Toolbar>
        </AppBar>
      )}

      <div className={classes.content} data-testid="note-drawer-content">
        {items.length === 0 ? (
          <div className={classes.noNotesContainer}>
            <Typography variant="h6">No Notes</Typography>
          </div>
        ) : (
          <>
            <Table className={classes.table}>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.tableCell}>
                    <TableSortLabel
                      active={sortColumn === 'insertedAtDerived'}
                      direction={
                        sortColumn === 'insertedAtDerived' ? sortOrder : 'asc'
                      }
                      onClick={() => handleSort('insertedAtDerived')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <TableSortLabel
                      active={sortColumn === 'note'}
                      direction={sortColumn === 'note' ? sortOrder : 'asc'}
                      onClick={() => handleSort('note')}
                    >
                      Note
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedItems.map(({ insertedAtDerived, note }) => (
                  <TableRow
                    key={insertedAtDerived}
                    className={classes.tableRow}
                  >
                    <TableCell className={classes.tableCell}>
                      {insertedAtDerived}
                    </TableCell>
                    <TableCell className={classes.tableCell}>{note}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={items.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </>
        )}
        <NoteWriter
          fspId={fspId}
          payerId={payerId}
          onClose={handleCloseNoteWriter}
          open={openNoteWriter}
        />
      </div>
    </ResizableDrawer>
  );
};

NoteDrawer.propTypes = {
  fspId: PropTypes.number,
  payerId: PropTypes.number,
  onClose: PropTypes.func,
  open: PropTypes.bool,
};

NoteDrawer.defaultProps = {
  fspId: null,
  payerId: null,
  onClose: always,
  open: false,
};

export default NoteDrawer;
